import React, { 
  useState, useEffect, useCallback, useRef, useMemo, memo 
} from 'react';
import * as THREE from 'three';

export const RENDERING_PROFILES = {
  // ── PROFIL ANATOMIQUE (Médecins, physiologistes) ─────────────────────────────
  anatomical: {
    name: "Anatomical",
    audience: "medical",
    description: "Éclairage clinique haute fidélité — mimique une salle de bloc opératoire.",
    
    theme: {
      bgPrimary: "#0a0e16",        // Quasi-noir avec léger bleu (comme murs bloc)
      bgSecondary: "#0f131f",
      accentPrimary: "#ff5577",    // Rouge cardiaque éclatant
      accentSecondary: "#00d9ff",  // Cyan diagnostic
      textPrimary: "#e8f0ff",      // Blanc bleuté très léger
      textSecondary: "#8fa3c0",
      grid: "rgba(0, 217, 255, 0.06)",
      border: "rgba(0, 217, 255, 0.12)",
    },

    lighting: {
      // Éclairage de bloc opératoire — très blanc, très net
      ambientColor: "#f5f8fb",        // Blanc froid
      ambientIntensity: 0.55,         // Plus haut que par défaut
      
      // Key light — mimique les scialytiques du bloc
      keyLightColor: "#eef8ff",
      keyLightIntensity: 1.8,
      keyLightPosition: { x: 3.5, y: 11, z: 5.2 },
      
      // Fill light — délimitation des ombres
      fillLightColor: "#b8d4ff",
      fillLightIntensity: 0.45,
      fillLightPosition: { x: -4, y: 1, z: -3 },
      
      // Rim light — contour anatomique
      rimLightColor: "#0088dd",
      rimLightIntensity: 0.35,
      rimLightPosition: { x: -5, y: 2.5, z: -6 },
      
      // Back light — contexte spatial
      backLightColor: "#004477",
      backLightIntensity: 0.15,
      backLightPosition: { x: 0, y: -8, z: 8 },
    },

    postprocessing: {
      bloomStrength: 0.35,          // Bloom modéré (pas trop de flou)
      bloomThreshold: 0.55,         // Seuil haut = pulsations réalistes seulement
      bloomRadius: 0.45,
      toneMappingExposure: 1.05,    // Légèrement surexposé = clarté chirurgicale
      toneMappingMode: "ACESFilmic",
      colorCorrectionSaturation: 1.15,  // Couleurs naturelles légèrement saturées
      colorCorrectionBrightness: 1.08,
      vignetteStrength: 0.08,       // Léger vignetage pour focus
      FXAA: true,
    },

    fog: {
      enabled: true,
      color: "#0a0e16",             // Changé de 0x0a0e16 à "#0a0e16"
      near: 18,
      far: 52,
    },

    particlesIntensity: 0.45,

    camera: {
      fov: 52,
      minDistance: 1.2,
      maxDistance: 32,
    },
  },

  // ── PROFIL TRADING (Traders, analystes — style Bloomberg Terminal) ──────────
  trading: {
    name: "Trading Terminal",
    audience: "financial",
    description: "Style Bloomberg Terminal — clarté maximale, contraste élevé, vert/ambre sur noir.",
    
    theme: {
      bgPrimary: "#000000",         // Noir pur (écran Bloomberg)
      bgSecondary: "#050808",
      accentPrimary: "#00ff00",     // Vert Bloomberg pur
      accentSecondary: "#ffcc00",   // Ambre warning
      accentTertiary: "#ff3300",    // Rouge rouge (pas de rose)
      textPrimary: "#00ff00",       // Vert monospace
      textSecondary: "#66ff00",     // Vert clair pour contexte
      grid: "rgba(0, 255, 0, 0.03)",
      border: "rgba(0, 255, 0, 0.08)",
    },

    lighting: {
      // Éclairage très plat — pas d'ombres dramatiques
      ambientColor: "#111111",
      ambientIntensity: 0.72,       // Haut = moins d'ombres
      
      // Key light — frontal et net
      keyLightColor: "#ccffcc",     // Vert clair
      keyLightIntensity: 1.5,
      keyLightPosition: { x: 2.2, y: 8.5, z: 4.8 },
      
      // Fill light — aplatissement du contraste
      fillLightColor: "#88ff88",
      fillLightIntensity: 0.62,
      fillLightPosition: { x: -3, y: 0.5, z: -2.5 },
      
      // Rim light — limité (pas de dramatique)
      rimLightColor: "#00cc00",
      rimLightIntensity: 0.18,
      rimLightPosition: { x: -4, y: 2, z: -5 },
      
      // Pas de back light (trop dramatique pour Bloomberg)
      backLightColor: "#000000",
      backLightIntensity: 0,
      backLightPosition: { x: 0, y: -5, z: 5 },  // Ajouté pour éviter les erreurs
    },

    postprocessing: {
      bloomStrength: 0.65,          // Bloom HAUT = effet CRT + glow phosphore
      bloomThreshold: 0.18,         // Seuil bas = tout brille légèrement
      bloomRadius: 0.88,            // Large bloom = effet écran cathodique rétro
      toneMappingExposure: 1.15,    // Plus clair que anatomical
      toneMappingMode: "ACESFilmic",
      colorCorrectionSaturation: 1.35,  // Hyper-saturé (vert pur)
      colorCorrectionBrightness: 1.15,  // Très clair
      vignetteStrength: 0,          // Pas de vignetage (écran uniforme)
      // Ajout: simulation CRT - scan lines subtiles
      crtEffect: {
        enabled: true,
        intensity: 0.08,
        scanlineFrequency: 1.2,
      },
      FXAA: true,
    },

    fog: {
      enabled: false,               // Pas de brouillard — transparence totale
      color: "#000000",             // Changé de 0x000000 à "#000000"
      near: 50,                     // Ajouté pour éviter les erreurs
      far: 100,                     // Ajouté pour éviter les erreurs
    },

    particlesIntensity: 0.1,        // Très peu de particules (distraction)

    camera: {
      fov: 48,                      // Légèrement plus étroit (focus analytique)
      minDistance: 2.5,
      maxDistance: 28,
    },

    // Style monospace renforcé pour trading
    typography: {
      mono: '"IBM Plex Mono", "Courier New", monospace',
      displayFont: '"IBM Plex Mono"',
      trackerLetter: 0.25,
    },
  },

  // ── PROFIL FORCE FIELD (Recherche, systèmes complexes) ──────────────────────
  forceField: {
    name: "Force Field",
    audience: "research",
    description: "Rendu sci-fi haute énergie — cyan/violet neon, champs de force visibles.",
    
    theme: {
      bgPrimary: "#0a0015",         // Noir violet très profond
      bgSecondary: "#1a0033",
      accentPrimary: "#00ffff",     // Cyan neon
      accentSecondary: "#ff00ff",   // Magenta neon
      accentTertiary: "#00ff88",    // Vert lime
      textPrimary: "#00ffff",
      textSecondary: "#88ffff",
      grid: "rgba(0, 255, 255, 0.08)",
      border: "rgba(255, 0, 255, 0.15)",
    },

    lighting: {
      // Éclairage sci-fi — sources multiples colorées
      ambientColor: "#1a0033",
      ambientIntensity: 0.35,       // Plus sombre pour la dramatique
      
      // Key light — cyan
      keyLightColor: "#00ffff",
      keyLightIntensity: 1.9,
      keyLightPosition: { x: 4, y: 9, z: 6 },
      
      // Fill light — magenta
      fillLightColor: "#ff00ff",
      fillLightIntensity: 0.55,
      fillLightPosition: { x: -5, y: 2, z: -4 },
      
      // Rim light — vert lime
      rimLightColor: "#00ff88",
      rimLightIntensity: 0.48,
      rimLightPosition: { x: -6, y: 3.5, z: -7 },
      
      // Back light — bleu électrique
      backLightColor: "#0088ff",
      backLightIntensity: 0.38,
      backLightPosition: { x: 0, y: -6, z: 9 },
    },

    postprocessing: {
      bloomStrength: 0.88,          // Bloom EXTRÊME = effet energétique
      bloomThreshold: 0.15,         // Seuil très bas = tout rayonne
      bloomRadius: 1.2,             // Très large = halo énergétique
      toneMappingExposure: 0.92,    // Légèrement sombre = neon sur noir
      toneMappingMode: "ACESFilmic",
      colorCorrectionSaturation: 1.6,    // Ultra saturé
      colorCorrectionBrightness: 0.95,
      vignetteStrength: 0.22,       // Vignetage marqué = tunnel énergétique
      // Ajout: distorsion géométrique subtile (champ de force)
      distortion: {
        enabled: true,
        intensity: 0.04,
        frequency: 0.3,
      },
      FXAA: true,
    },

    fog: {
      enabled: true,
      color: "#0a0015",             // Changé de 0x0a0015 à "#0a0015"
      near: 10,
      far: 40,
    },

    particlesIntensity: 0.8,        // Beaucoup de particules = énergie

    camera: {
      fov: 54,
      minDistance: 1.5,
      maxDistance: 35,
    },
  },
};

export const getDesignSystem = memo((profile) => {
  if (!profile) {
    return null;
  }
 
  return {
    colors: {
      bg: profile.theme.bgPrimary,
      bgPanel: profile.theme.bgSecondary,
      bgCard: profile.theme.bgSecondary,
      bgInset: profile.theme.bgPrimary,
      grid: profile.theme.grid,
      border: profile.theme.border,
      borderHover: profile.theme.border.replace('0.12', '0.25'),
      borderActive: profile.theme.border.replace('0.12', '0.55'),
      
      cyan: profile.theme.accentPrimary,
      green: profile.theme.accentTertiary || profile.theme.accentPrimary,
      amber: profile.theme.accentSecondary,
      red: '#ff4d6d',
      
      textPrimary: profile.theme.textPrimary,
      textSecond: profile.theme.textSecondary,
      textMuted: '#7a8db5',
      textAccent: profile.theme.accentPrimary,
    },
    fonts: {
      mono: '"JetBrains Mono", "Courier New", monospace',
      display: '"Syne", "Space Grotesk", system-ui, sans-serif',
      body: '"Inter", system-ui, sans-serif',
    },
  };
});

// ═══════════════════════════════════════════════════════════════════════════════
// FONCTION D'ACCÈS ET DE SÉLECTION
// ═══════════════════════════════════════════════════════════════════════════════

export const getRenderingProfile = (sceneType = "anatomical") => {
  const typeMap = {
    anatomical: "anatomical",
    medical: "anatomical",
    heart: "anatomical",
    coeur: "anatomical",
    
    trading: "trading",
    trader: "trading",
    finance: "trading",
    market: "trading",
    marche: "trading",
    
    forceField: "forceField",
    forcefield: "forceField",
    research: "forceField",
    complex: "forceField",
  };

  const profileKey = typeMap[sceneType?.toLowerCase()] || "anatomical";
  return RENDERING_PROFILES[profileKey];
};

// ═══════════════════════════════════════════════════════════════════════════════
// PRÉSETS DE CONFIGURATION THREE.JS
// ═══════════════════════════════════════════════════════════════════════════════

export const buildLightingConfig = (profile) => {
  // Valeurs par défaut complètes
  const DEFAULT_LIGHTING = {
    ambientColor: 0xffffff,
    ambientIntensity: 0.5,
    keyLightColor: 0xffffff,
    keyLightIntensity: 1.0,
    keyLightPosition: { x: 5, y: 5, z: 5 },
    fillLightColor: 0xffffff,
    fillLightIntensity: 0.3,
    fillLightPosition: { x: -3, y: 1, z: 3 },
    rimLightColor: 0xffffff,
    rimLightIntensity: 0.3,
    rimLightPosition: { x: -5, y: 2, z: -5 },
    backLightColor: 0xffffff,
    backLightIntensity: 0,
    backLightPosition: { x: 0, y: -5, z: 5 }
  };
  
  // Fusionner avec les valeurs par défaut
  const lighting = {
    ...DEFAULT_LIGHTING,
    ...(profile?.lighting || {})
  };
  
  // S'assurer que toutes les positions sont complètes
  const ensurePosition = (pos, defaultPos) => ({
    x: pos?.x ?? defaultPos.x,
    y: pos?.y ?? defaultPos.y,
    z: pos?.z ?? defaultPos.z
  });
  
  return {
    ambient: {
      color: lighting.ambientColor,
      intensity: lighting.ambientIntensity,
    },
    key: {
      color: lighting.keyLightColor,
      intensity: lighting.keyLightIntensity,
      position: ensurePosition(lighting.keyLightPosition, DEFAULT_LIGHTING.keyLightPosition),
      castShadow: true,
      shadowMapSize: 2048,
    },
    fill: {
      color: lighting.fillLightColor,
      intensity: lighting.fillLightIntensity,
      position: ensurePosition(lighting.fillLightPosition, DEFAULT_LIGHTING.fillLightPosition),
    },
    rim: {
      color: lighting.rimLightColor,
      intensity: lighting.rimLightIntensity,
      position: ensurePosition(lighting.rimLightPosition, DEFAULT_LIGHTING.rimLightPosition),
    },
    back: {
      color: lighting.backLightColor,
      intensity: lighting.backLightIntensity,
      position: ensurePosition(lighting.backLightPosition, DEFAULT_LIGHTING.backLightPosition),
    },
  };
};



export const buildPostprocessingConfig = (profile) => {
  const pp = profile.postprocessing;
  return {
    bloom: {
      strength: pp.bloomStrength,
      threshold: pp.bloomThreshold,
      radius: pp.bloomRadius,
    },
    toneMapping: {
      exposure: pp.toneMappingExposure,
      mode: pp.toneMappingMode,
    },
    colorCorrection: {
      saturation: pp.colorCorrectionSaturation,
      brightness: pp.colorCorrectionBrightness,
    },
    vignette: {
      strength: pp.vignetteStrength,
    },
    crt: pp.crtEffect,
    distortion: pp.distortion,
    fxaa: pp.FXAA,
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER: APPLIQUER UN PROFIL À UNE SCÈNE
// ═══════════════════════════════════════════════════════════════════════════════

export const applyRenderingProfile = (scene, camera, renderer, composer, sceneType) => {
  const profile = getRenderingProfile(sceneType);
  
  if (!profile) {
    console.warn(`Profil inconnu: ${sceneType}, fallback anatomical`);
    return;
  }

  // 1. Background et fog
  const bgColor = new THREE.Color(profile.theme.bgPrimary);
  scene.background = bgColor;
  scene.fog = new THREE.Fog(
    bgColor,
    profile.fog.near,
    profile.fog.far
  );

  // 2. Éclairage
  const lightingCfg = buildLightingConfig(profile);
  
  // Ambient light
  const ambient = new THREE.AmbientLight(
    lightingCfg.ambient.color,
    lightingCfg.ambient.intensity
  );
  scene.add(ambient);

  // Key light
  const keyLight = new THREE.DirectionalLight(
    lightingCfg.key.color,
    lightingCfg.key.intensity
  );
  keyLight.position.set(
    lightingCfg.key.position.x,
    lightingCfg.key.position.y,
    lightingCfg.key.position.z
  );
  if (lightingCfg.key.castShadow) {
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.width = lightingCfg.key.shadowMapSize;
    keyLight.shadow.mapSize.height = lightingCfg.key.shadowMapSize;
    keyLight.shadow.bias = -0.0002;
  }
  scene.add(keyLight);

  // Fill light
  const fillLight = new THREE.DirectionalLight(
    lightingCfg.fill.color,
    lightingCfg.fill.intensity
  );
  fillLight.position.set(
    lightingCfg.fill.position.x,
    lightingCfg.fill.position.y,
    lightingCfg.fill.position.z
  );
  scene.add(fillLight);

  // Rim light
  const rimLight = new THREE.DirectionalLight(
    lightingCfg.rim.color,
    lightingCfg.rim.intensity
  );
  rimLight.position.set(
    lightingCfg.rim.position.x,
    lightingCfg.rim.position.y,
    lightingCfg.rim.position.z
  );
  scene.add(rimLight);

  // Back light (si intensité > 0)
  if (lightingCfg.back.intensity > 0) {
    const backLight = new THREE.DirectionalLight(
      lightingCfg.back.color,
      lightingCfg.back.intensity
    );
    backLight.position.set(
      lightingCfg.back.position.x,
      lightingCfg.back.position.y,
      lightingCfg.back.position.z
    );
    scene.add(backLight);
  }

  // 3. Tone mapping
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = lightingCfg.toneMapping.exposure;

  // 4. Retourner la config pour post-processing
  return {
    profile,
    lighting: lightingCfg,
    postprocessing: buildPostprocessingConfig(profile),
  };
};

export default RENDERING_PROFILES;